import React from 'react';
import {
  Box,
  Typography,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import CodeIcon from '@material-ui/icons/Code';
import ExtensionIcon from '@material-ui/icons/Extension';
import SettingsInputComponentIcon from '@material-ui/icons/SettingsInputComponent';
import { HomeCard } from './HomeCard';
import { CatalogSummaryGrid } from './CatalogSummaryGrid';
import { CatalogItem, EntityKind } from './CatalogItem';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  recentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  recentTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  recentList: {
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

type RecentEntity = {
  kind: EntityKind;
  name: string;
  namespace?: string;
  owner?: string;
  updatedAt?: string;
};

type CatalogSummaryCardProps = {
  servicesCount?: number;
  apisCount?: number;
  componentsCount?: number;
  recentEntities?: RecentEntity[];
  loading?: boolean;
  actionHref?: string;
  onEntityClick?: (entity: RecentEntity) => void;
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const CatalogSummaryCard = ({
  servicesCount = 0,
  apisCount = 0,
  componentsCount = 0,
  recentEntities = [],
  loading = false,
  actionHref = '/catalog',
  onEntityClick,
}: CatalogSummaryCardProps) => {
  const classes = useStyles();

  const summaryItems = [
    {
      icon: <CodeIcon style={{ fontSize: 18 }} />,
      label: 'Services',
      count: servicesCount,
      iconColor: '#5292F4',
    },
    {
      icon: <ExtensionIcon style={{ fontSize: 18 }} />,
      label: 'APIs',
      count: apisCount,
      iconColor: '#F0A31C',
    },
    {
      icon: <SettingsInputComponentIcon style={{ fontSize: 18 }} />,
      label: 'Components',
      count: componentsCount,
      iconColor: '#22C55E',
    },
  ];

  return (
    <HomeCard
      title="Catalog Summary"
      actionLabel="Browse Catalog"
      actionHref={actionHref}
      fitContent
    >
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box className={classes.content}>
          <CatalogSummaryGrid items={summaryItems} />

          <Box>
            <Box className={classes.recentHeader}>
              <Typography className={classes.recentTitle}>
                Recently Updated
              </Typography>
            </Box>
            <Box className={classes.recentList}>
              {recentEntities.length > 0 ? (
                recentEntities.map((entity, idx) => (
                  <CatalogItem
                    key={`${entity.kind}-${entity.namespace || 'default'}-${
                      entity.name
                    }-${idx}`}
                    kind={entity.kind}
                    name={entity.name}
                    owner={entity.owner}
                    time={
                      entity.updatedAt
                        ? formatRelativeTime(entity.updatedAt)
                        : undefined
                    }
                    href={`/catalog/${
                      entity.namespace || 'default'
                    }/${entity.kind.toLowerCase()}/${entity.name}`}
                    onClick={
                      onEntityClick ? () => onEntityClick(entity) : undefined
                    }
                  />
                ))
              ) : (
                <Box className={classes.emptyState}>
                  <Typography className={classes.emptyText}>
                    No recent updates
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </HomeCard>
  );
};
